import { Marker } from "maplibre-gl";
import type { Map } from "maplibre-gl";
import { displayName } from "../../../lib/osm/identity.ts";
import type { OsmElement } from "../../../lib/osm.ts";
import type { PoiSearchState } from "../../../lib/search/controller.ts";
import type { OsmSelectionTarget } from "../selection/osmLookup.ts";

export interface PoiSearchResultsPresentation {
  setSearchState: (state: PoiSearchState) => void;
  // Search results and current selection evolve independently, but the map
  // should not show both a background result pin and the "selected POI" marker
  // for the same element at once.
  setActiveSelection: (selection: OsmElement) => void;
  // Clears the selection-derived hide state so current search results can all
  // render again.
  clearActiveSelection: () => void;
  destroy: () => void;
}

interface PoiSearchResultsPresentationOptions {
  // These injections exist only to keep focused unit tests decoupled from
  // MapLibre's concrete Marker class and the browser DOM.
  createMarker?: (element: HTMLElement) => Marker;
  createMarkerElement?: (
    options: PoiSearchResultMarkerElementOptions,
  ) => HTMLElement;
  onSelect?: (selectionTarget: ExactSelectionTarget) => void;
}

interface PoiSearchResultMarkerElementOptions {
  label: string | undefined;
  labelVisibility: "always" | "hover";
}

const POI_SEARCH_LABEL_LIMIT = 50;
const VISIBLE_MARKER_LABEL_MAX_LENGTH = 38;
const RESULT_PIN_URL = new URL(
  "../../../assets/map-pins/result-pin.svg",
  import.meta.url,
).href;

type ExactSelectionTarget = Extract<OsmSelectionTarget, { kind: "element" }>;

export function attachPoiSearchResultsPresentation(
  map: Map,
  options: PoiSearchResultsPresentationOptions = {},
): PoiSearchResultsPresentation {
  const createMarker = options.createMarker ??
    ((element) =>
      new Marker({
        element,
        anchor: "bottom",
      }));
  const createMarkerElement = options.createMarkerElement ??
    defaultMarkerElement;
  let state: PoiSearchState = { status: "idle" };
  let activeSelection: OsmElement | undefined;
  let markers: Marker[] = [];

  function clearMarkers(): void {
    for (const marker of markers) {
      marker.remove();
    }
    markers = [];
  }

  function render(): void {
    clearMarkers();
    if (state.status !== "success") return;

    const labelVisibility = state.results.length <= POI_SEARCH_LABEL_LIMIT
      ? "always"
      : "hover";

    for (const result of state.results) {
      if (
        activeSelection &&
        result.type === activeSelection.type &&
        result.id === activeSelection.id
      ) {
        continue;
      }

      const selectionTarget: ExactSelectionTarget = {
        kind: "element",
        type: result.type,
        id: result.id,
        lngLat: result.lnglat,
      };
      const element = createMarkerElement({
        label: displayName(result.tags),
        labelVisibility,
      });
      element.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        // The presenter owns result-marker visibility, so it hides the clicked
        // marker immediately instead of waiting for selection state to
        // round-trip through the exact lookup flow.
        activeSelection = {
          type: result.type,
          id: result.id,
        };
        render();
        options.onSelect?.(selectionTarget);
      });

      markers.push(
        createMarker(element)
          .setLngLat(result.lnglat)
          .addTo(map),
      );
    }
  }

  function setSearchState(next: PoiSearchState): void {
    state = next;
    render();
  }

  function setActiveSelection(next: OsmElement): void {
    activeSelection = next;
    render();
  }

  function clearActiveSelection(): void {
    activeSelection = undefined;
    render();
  }

  function destroy(): void {
    clearMarkers();
  }

  return {
    setSearchState,
    setActiveSelection,
    clearActiveSelection,
    destroy,
  };
}

function defaultMarkerElement(
  options: PoiSearchResultMarkerElementOptions,
): HTMLElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = [
    "group relative block h-[30px] w-[21px] cursor-pointer",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/75 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  ].join(" ");
  button.setAttribute(
    "aria-label",
    options.label ? `Select ${options.label}` : "Select POI search result",
  );

  const img = document.createElement("img");
  img.src = RESULT_PIN_URL;
  img.alt = "";
  img.width = 21;
  img.height = 30;
  img.draggable = false;
  img.className = "block h-[30px] w-[21px]";

  button.append(img);
  if (options.label) {
    const label = document.createElement("span");
    label.className = [
      "pointer-events-none absolute top-1/2 left-[calc(100%+0.4rem)] max-h-[2lh] w-[min(10rem,50vw)] -translate-y-1/2 overflow-hidden break-words text-left text-xs font-semibold leading-tight text-foreground [text-shadow:0_1px_1px_var(--background),0_-1px_1px_var(--background),1px_0_1px_var(--background),-1px_0_1px_var(--background),0_0_2px_var(--background)]",
      options.labelVisibility === "hover"
        ? "translate-x-1 opacity-0 transition duration-150 ease-out group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100"
        : "",
    ].join(" ");
    label.textContent = visibleMarkerLabel(options.label);
    button.append(label);
  }

  return button;
}

// CSS multi-line ellipsis still relies on vendor-specific line-clamp rules.
function visibleMarkerLabel(label: string): string {
  if (label.length <= VISIBLE_MARKER_LABEL_MAX_LENGTH) return label;

  const shortened = label.slice(0, VISIBLE_MARKER_LABEL_MAX_LENGTH - 3)
    .trimEnd();
  const wordBoundary = shortened.lastIndexOf(" ");
  const prefix =
    wordBoundary >= Math.floor(VISIBLE_MARKER_LABEL_MAX_LENGTH * 0.6)
      ? shortened.slice(0, wordBoundary)
      : shortened;

  return `${prefix}...`;
}
